import { css } from "@linaria/core";
import React, { useMemo } from "react";
import { colors, fonts, spacings } from "@ui/Theme";
import type { FailureValueStatus } from "../../../../../api/useFailureMetadata";
import t from "../../../../../i18n";
import type { Action as TooltipAction } from "@ui/Highcharts/ui/Tooltip";
import classNames from "classnames";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text from "@ui/Text";
import { Action } from "..";
import { getFaultStatusText, getFaultStatusTextIcon } from "../../../Fault";

export const FaultStatusChartTooltip = ({
  statusHover,
  statusMultipleSelected,
  selectedCount,
  automaticDiagnosisRelaunch,
}: {
  statusHover: FailureValueStatus;
  statusMultipleSelected: FailureValueStatus[];
  selectedCount: number;
  automaticDiagnosisRelaunch: boolean;
}) => {
  const icon = getFaultStatusTextIcon(statusHover);
  const statusTranslate = getFaultStatusText(statusHover);

  const statusSelected = useMemo(() => new Set(statusMultipleSelected), [statusMultipleSelected]);
  const setStatusCommun: Set<FailureValueStatus> = useMemo(
    () => new Set(["TO_BE_ANALYSED", "SUSPECTED", "CONFIRMED"]),
    [],
  );
  const setStatusRejected: Set<FailureValueStatus> = useMemo(() => new Set(["REJECTED"]), []);
  const setStatusLegacyRejected: Set<FailureValueStatus> = useMemo(
    () => new Set(["LEGACY_REJECTED"]),
    [],
  );

  const intersectionWithCommun = useMemo(
    () => new Set([...setStatusCommun].filter(value => statusSelected.has(value))),
    [setStatusCommun, statusSelected],
  );
  const intersectionWithRejected = useMemo(
    () => new Set([...setStatusRejected].filter(value => statusSelected.has(value))),
    [setStatusRejected, statusSelected],
  );
  const intersectionWithLegacyRejected = useMemo(
    () => new Set([...setStatusLegacyRejected].filter(value => statusSelected.has(value))),
    [setStatusLegacyRejected, statusSelected],
  );

  const hasSelectedIncompatibleStatus =
    (intersectionWithCommun.size > 0 && intersectionWithRejected.size > 0) ||
    (intersectionWithCommun.size > 0 && intersectionWithLegacyRejected.size > 0) ||
    (intersectionWithRejected.size > 0 && intersectionWithLegacyRejected.size > 0);

  const statusActions: TooltipAction<FailureValueStatus>[] = useMemo(
    () =>
      !hasSelectedIncompatibleStatus &&
      !(statusSelected.has("REJECTED") && statusSelected.has("LEGACY_REJECTED"))
        ? [
            {
              text: t("diagnosis.chart.tooltip.button.to_be_analysed"),
              action: "TO_BE_ANALYSED",
              intent: "confirm",
              show: statusSelected.has("REJECTED"),
            },
            {
              text: t("diagnosis.chart.tooltip.button.confirmed"),
              action: "CONFIRMED",
              intent: "confirm",
              show:
                statusSelected.has("TO_BE_ANALYSED") ||
                statusSelected.has("SUSPECTED") ||
                statusSelected.has("REJECTED"),
            },
            {
              text: t("diagnosis.chart.tooltip.button.suspected"),
              action: "SUSPECTED",
              intent: "confirm",
              show:
                statusSelected.has("TO_BE_ANALYSED") ||
                statusSelected.has("CONFIRMED") ||
                statusSelected.has("REJECTED") ||
                statusSelected.has("LEGACY_REJECTED"),
            },
            {
              text: t("diagnosis.chart.tooltip.button.false_detection"),
              action: "REJECTED",
              intent: "confirm",
              show:
                statusSelected.has("SUSPECTED") ||
                statusSelected.has("TO_BE_ANALYSED") ||
                statusSelected.has("CONFIRMED") ||
                statusSelected.has("REJECTED"),
            },
          ]
        : [],
    [statusSelected, hasSelectedIncompatibleStatus],
  );

  if (hasSelectedIncompatibleStatus)
    return (
      <div className={faultStatusCss}>
        <AdditionnalMessage
          status="WARNING"
          text={t(
            "diagnosis.chart.tooltip.message.incompatible_status",
            statusMultipleSelected.filter(s => s === "REJECTED" || s === "LEGACY_REJECTED").length,
          )}
        />
      </div>
    );

  return (
    <div className={faultStatusCss}>
      {selectedCount <= 1 ? (
        <div className={faultStatusIconCss}>
          {icon}
          <Text type="body1SemiBold">{statusTranslate}</Text>
        </div>
      ) : null}

      {statusSelected.has("LEGACY_REJECTED") || statusHover === "LEGACY_REJECTED" ? (
        <div className={faultStatusLegacyRejectedCss}>
          <AdditionnalMessage
            status="INFO"
            text={t("diagnosis.chart.tooltip.message.relaunch_diagnostic")}
          />
          {statusActions.length && selectedCount ? (
            <Action
              key="REJECTED"
              className={classNames({ ["confirm"]: true })}
              data-action="REJECTED"
            >
              {t("diagnosis.chart.tooltip.status.action.relaunch_diagnostic")}
            </Action>
          ) : null}
        </div>
      ) : null}

      {statusActions.length && selectedCount ? (
        <>
          {statusSelected.has("REJECTED") ? (
            <Action
              key="BACK_TO_BE_ANALYSED"
              className={classNames({ ["confirm"]: true })}
              data-action="BACK_TO_BE_ANALYSED"
            >
              {t("diagnosis.chart.tooltip.status.action.reintegrate_in_diagnosis")}
            </Action>
          ) : (
            <>
              <div>
                <p className={faultUpdate}>
                  {t("diagnosis.chart.tooltip.subtitle_link.update_status")}
                </p>
                <div className={statusActionsCss}>
                  {statusActions
                    .filter(a => a.show)
                    .map(a => {
                      const action =
                        a.action === "REJECTED"
                          ? automaticDiagnosisRelaunch === true
                            ? "REJECTED"
                            : "LEGACY_REJECTED"
                          : a.action;
                      return (
                        <Action
                          key={action}
                          className={classNames({ [a.intent]: true })}
                          data-action={action}
                        >
                          {a.text}
                        </Action>
                      );
                    })}
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

const faultUpdate = css`
  font: ${fonts.body2};
  color: ${colors.carbon700};
  padding-bottom: 8px;
`;
const faultStatusCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;
const faultStatusIconCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.carbon900};
`;
const statusActionsCss = css`
  display: flex;
  font: ${fonts.body2};
  gap: ${spacings.xs};
  color: ${colors.carbon900};
`;
const faultStatusLegacyRejectedCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;
